import { useCallback, useRef, useEffect } from 'react';

export function useChatScroll<T>(
  messages?: T[],
  dependencies: any[] = [],
  roomName?: string
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesLengthRef = useRef(0);
  const isFirstLoad = useRef(true);
  const previousRoomRef = useRef<string | undefined>();

  const scrollToBottom = useCallback((smooth: boolean = true) => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    // For column-reverse, scrollTop 0 is the bottom
    container.scrollTo({
      top: 0,
      behavior: smooth ? 'smooth' : 'auto',
    });
  }, []);

  const instantScrollToBottom = useCallback(() => {
    if (!containerRef.current) return;
    // For column-reverse, 0 is the bottom
    containerRef.current.scrollTop = 0;
  }, []);

  // Handle initial load and room changes
  useEffect(() => {
    if (!containerRef.current) return;

    // On room change or first load, no need to scroll - column-reverse handles it
    if (roomName !== previousRoomRef.current) {
      previousRoomRef.current = roomName;
      messagesLengthRef.current = messages?.length || 0;
      isFirstLoad.current = true;
      // Column-reverse automatically shows bottom, no scroll needed
      return;
    }

    // For new messages after initial load
    if (messages && messages.length > messagesLengthRef.current && !isFirstLoad.current) {
      messagesLengthRef.current = messages.length;

      // Check if user is near bottom (within 100px)
      const container = containerRef.current;
      const isNearBottom = container.scrollTop <= 100;

      // Only auto-scroll if user is near bottom
      if (isNearBottom) {
        requestAnimationFrame(() => {
          scrollToBottom(true);
        });
      }
    }

    // Mark initial load as complete after first render
    if (isFirstLoad.current && messages && messages.length > 0) {
      isFirstLoad.current = false;
    }
  }, [messages, roomName, scrollToBottom]);

  return { containerRef, scrollToBottom, instantScrollToBottom };
}